import React from 'react';
import { cn } from '../../utils/cn';
import { CHIP_DENSE } from '../../utils/chipColors';

/**
 * BasketNameLink — shared basket-name renderer for the All Allocations page
 * and Schedule viewer.
 *
 * Why this exists:
 *  - User-owned baskets are clickable links into BasketsPage.
 *  - Marketplace-subscribed baskets live under the admin/publisher's partition,
 *    so we DON'T link them into the subscriber's BasketsPage (the row wouldn't
 *    be found). We render them in violet with an "MP" pill so subscribers can
 *    spot them at a glance.
 *
 * Callers decide whether marketplace rows are clickable:
 *  - All Allocations page → `onClick={undefined}` for marketplace (no link).
 *  - Schedule viewer      → `onClick={() => onOpenBasket(...)}` always (existing
 *                            behaviour preserved per product decision).
 *
 * If `onClick` is undefined the basket name is rendered as plain text.
 */

export interface BasketNameLinkProps {
  basketId: string;
  basketName: string;
  /** True when allocation.source === 'MARKETPLACE'. */
  isMarketplace?: boolean;
  /** Click handler; if omitted the name renders as non-clickable text. */
  onClick?: () => void;
  /** Visual size: `sm` (default — schedule density) or `md` (allocation cards / tables). */
  size?: 'sm' | 'md';
  /** Optional title attribute override (defaults are sensible). */
  title?: string;
  className?: string;
}

const BasketNameLink: React.FC<BasketNameLinkProps> = ({
  basketId: _basketId,
  basketName,
  isMarketplace = false,
  onClick,
  size = 'md',
  title,
  className,
}) => {
  const textSize = size === 'sm' ? 'text-[11px]' : 'text-sm';
  const fontWeight = size === 'sm' ? '' : 'font-medium';

  // Colour: violet for marketplace, blue for user-owned (matches schedule
  // viewer's existing inline convention).
  const baseColour = isMarketplace
    ? 'text-violet-700 dark:text-violet-300'
    : 'text-gray-900 dark:text-white';
  const hoverColour = isMarketplace
    ? 'hover:text-violet-800 dark:hover:text-violet-200'
    : 'hover:text-blue-700 dark:hover:text-blue-300';
  const linkColour = isMarketplace
    ? 'text-violet-600 dark:text-violet-400'
    : 'text-blue-600 dark:text-blue-400';

  const resolvedTitle =
    title ??
    (isMarketplace
      ? 'Marketplace subscription — basket is published by another publisher'
      : undefined);

  const nameNode = onClick ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        textSize,
        fontWeight,
        isMarketplace ? linkColour : 'text-blue-600 dark:text-blue-400',
        hoverColour,
        'hover:underline transition-colors text-left truncate',
      )}
      title={resolvedTitle}
    >
      {basketName || '—'}
    </button>
  ) : (
    <span
      className={cn(textSize, fontWeight, baseColour, 'truncate')}
      title={resolvedTitle}
    >
      {basketName || '—'}
    </span>
  );

  return (
    <span className={cn('inline-flex items-center gap-1.5 min-w-0', className)}>
      {nameNode}
      {isMarketplace && (
        <span
          className={cn(
            'inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider shrink-0',
            CHIP_DENSE.MARKETPLACE,
          )}
          title="Marketplace subscription"
          aria-label="Marketplace subscription"
        >
          MP
        </span>
      )}
    </span>
  );
};

export default BasketNameLink;
